import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Milliseconds until this JWT's `exp` claim fires. 0 if the token is missing
 * an exp claim, is malformed, or is already expired.
 *
 * Lives here (not in the console app) so any host of AuthProvider gets the
 * same watchdog behaviour — currently the console app is the only consumer,
 * but portal-api and vex-console will share this shell package.
 */
function jwtExpiresInMs(token: string): number {
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return 0;
  try {
    const b64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(parts[1].length / 4) * 4, '=');
    const claims = JSON.parse(atob(b64)) as { exp?: number };
    if (typeof claims.exp !== 'number') return 0;
    const ms = claims.exp * 1000 - Date.now();
    return ms > 0 ? ms : 0;
  } catch {
    return 0;
  }
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  homeTenant: string;
  /**
   * Home region embedded in the auth session (multi-region hardening P1).
   * vex-auth stamps this into every issued JWT under the `region` claim; the
   * console seeds `scope.region` from it on login so the customer never lands
   * on an "empty region" state that would cause SDK calls to hit the wrong
   * gate. Empty for legacy tokens issued before P1.
   */
  homeRegion: string;
  roles: ReadonlyArray<string>;
}

export interface SignUpPayload {
  companyName: string;
  tenant: string;
  username: string;
  email: string;
  password: string;
  displayName?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Register a new tenant + root user in one shot. Available when the
   * adapter implements `signUp` (httpAuthAdapter does; the mock adapter
   * does not). Throws if the adapter is signup-less.
   */
  signUp: (payload: SignUpPayload) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthAdapter {
  /** POST credentials → server cookie + returns user profile + token. */
  signIn(username: string, password: string): Promise<{ user: AuthUser; token: string }>;
  /** Drop the cookie + revoke the token server-side. */
  signOut(): Promise<void>;
  /** Re-resolve the current session from the cookie (boot, page reload). */
  whoami(): Promise<{ user: AuthUser; token: string } | null>;
  /**
   * Public tenant registration — creates a new tenant + root user and
   * returns an activated session. Optional so mock adapters don't have to
   * implement it; production adapters (httpAuthAdapter) do.
   */
  signUp?(payload: SignUpPayload): Promise<{ user: AuthUser; token: string }>;
}

export function AuthProvider({ adapter, children }: { adapter: AuthAdapter; children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void adapter
      .whoami()
      .then((session) => {
        if (!alive) return;
        if (session) {
          setUser(session.user);
          setToken(session.token);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [adapter]);

  // Token-expiry watchdog. When we hold a JWT with an `exp` claim, schedule
  // a hard signOut for that timestamp. Converts "silent 401 mid-task" into
  // "you're back at the login screen because your session ended". Cleared
  // on token change / provider unmount.
  useEffect(() => {
    if (!token) return;
    const expMs = jwtExpiresInMs(token);
    if (expMs === 0) return;
    const id = window.setTimeout(() => {
      void adapter
        .signOut()
        .catch(() => {
          /* clear locally even if the network signOut fails */
        })
        .finally(() => {
          setUser(null);
          setToken(null);
        });
    }, expMs);
    return () => window.clearTimeout(id);
  }, [token, adapter]);

  // Cross-tab expiry: if another tab clears the token (localStorage), or an
  // external unauthorized event fires, drop this tab's session immediately.
  useEffect(() => {
    const onExternalSignOut = () => {
      setUser(null);
      setToken(null);
    };
    window.addEventListener('vex:auth:unauthorized', onExternalSignOut);
    return () => window.removeEventListener('vex:auth:unauthorized', onExternalSignOut);
  }, []);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const s = await adapter.signIn(username, password);
      setUser(s.user);
      setToken(s.token);
    },
    [adapter],
  );

  const signOut = useCallback(async () => {
    await adapter.signOut();
    setUser(null);
    setToken(null);
  }, [adapter]);

  const signUp = useCallback(
    async (payload: SignUpPayload) => {
      if (!adapter.signUp) {
        throw new Error('signUp is not supported by the current auth adapter');
      }
      const s = await adapter.signUp(payload);
      setUser(s.user);
      setToken(s.token);
    },
    [adapter],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, loading, signIn, signOut, signUp }),
    [user, token, loading, signIn, signOut, signUp],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function createMockAuthAdapter(user: AuthUser): AuthAdapter {
  return {
    async signIn() {
      await tick();
      return { user, token: 'mock-jwt-token' };
    },
    async signOut() {
      await tick();
    },
    async whoami() {
      await tick();
      return { user, token: 'mock-jwt-token' };
    },
  };
}

const tick = () => new Promise((r) => setTimeout(r, 50));
